/**
 * Browser-side media derivatives.
 *
 * The original is what the host downloads. The thumbnail is what the wall, the
 * gallery and the moderation queue actually render — and what Workers AI reads.
 * Generating it here rather than in the Worker is deliberate:
 *
 *   - no image decoder or WASM bundle in the Worker, so the upload path stays
 *     inside the free CPU budget
 *   - the phone already has the pixels decoded; a 720px WebP is a few
 *     milliseconds of canvas work
 *   - the derivative travels in the same request as the original, so there is
 *     no second round trip to a paid image service
 *
 * HEIC is the one format browsers cannot agree on. Safari decodes it natively;
 * Chrome and Firefox do not. `heic2any` is imported dynamically only when a HEIC
 * file is actually selected, so the 2 MB WASM decoder never reaches a guest who
 * is uploading JPEGs.
 */

import { LIMITS } from "@shared";

import { prepareImage } from "./upload-prepare.js";

export interface PreparedMedia {
  /** The file to upload. HEIC is converted to JPEG, so this may differ. */
  readonly file: File;
  readonly width: number;
  readonly height: number;
  readonly durationSec: number;
  /** 720px WebP/JPEG derivative, or null when one cannot be produced. */
  readonly thumb: Blob | null;
}

const HEIC_TYPE = /^image\/hei[cf]$/i;
const HEIC_NAME = /\.hei[cf]$/i;

/**
 * Decode anything the browser will give us.
 *
 * `createImageBitmap` is the fast path and handles EXIF orientation correctly.
 * The `<img>` fallback exists for Safari versions where the bitmap constructor
 * is present but rejects certain HEIC variants.
 *
 * Returns null rather than throwing: "this browser has no decoder for it" is a
 * policy state the upload survives (the original is sent, the derivative is
 * dropped), not an error to investigate.
 */
async function decodeImage(file: File): Promise<ImageBitmap | HTMLImageElement | null> {
  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    const url = URL.createObjectURL(file);
    try {
      return await new Promise<HTMLImageElement | null>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
      });
    } finally {
      // The decoded element keeps its own copy; revoking here is safe.
      URL.revokeObjectURL(url);
    }
  }
}

function dimensions(source: ImageBitmap | HTMLImageElement): { width: number; height: number } {
  return source instanceof ImageBitmap
    ? { width: source.width, height: source.height }
    : { width: source.naturalWidth, height: source.naturalHeight };
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Scale into a thumbnail, trying WebP first and JPEG second.
 *
 * Some Safari builds silently return PNG when asked for WebP. A 720px PNG of a
 * photograph can be larger than the Worker's thumbnail cap, so the result is
 * checked and re-encoded rather than trusted by MIME type.
 */
async function renderThumb(
  source: CanvasImageSource,
  width: number,
  height: number,
): Promise<Blob | null> {
  if (width <= 0 || height <= 0) return null;

  for (const edge of [LIMITS.thumbMaxEdge, 480]) {
    const scale = Math.min(1, edge / Math.max(width, height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);

    for (const [type, quality] of [
      ["image/webp", 0.82],
      ["image/jpeg", 0.8],
      ["image/jpeg", 0.68],
    ] as const) {
      const blob = await canvasToBlob(canvas, type, quality);
      if (blob && blob.size > 0 && blob.size <= LIMITS.maxThumbBytes) return blob;
    }
  }

  return null;
}

/** Convert a HEIC/HEIF file to JPEG so every browser and the AI model can read it. */
async function normaliseHeic(file: File): Promise<File> {
  if (!HEIC_TYPE.test(file.type) && !HEIC_NAME.test(file.name)) return file;

  try {
    const { default: heic2any } = await import("heic2any");
    const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
    const blob = Array.isArray(converted) ? converted[0] : converted;
    if (!blob) return file;

    return new File([blob], file.name.replace(HEIC_NAME, ".jpg"), {
      type: "image/jpeg",
      lastModified: file.lastModified,
    });
  } catch {
    // The Worker accepts HEIC; only the derivative and cross-browser preview
    // are lost. Returning the original is still better than blocking the send.
    return file;
  }
}

/** Pull one frame out of a video for the poster. */
async function videoPoster(
  file: File,
): Promise<{ thumb: Blob | null; width: number; height: number; durationSec: number }> {
  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.preload = "metadata";
  video.muted = true;
  video.playsInline = true;
  video.src = url;

  try {
    const meta = await new Promise<{ width: number; height: number; duration: number }>(
      (resolve, reject) => {
        const timer = setTimeout(() => reject(new Error("Video metadata timed out.")), 15_000);
        video.onloadedmetadata = () => {
          clearTimeout(timer);
          resolve({
            width: video.videoWidth,
            height: video.videoHeight,
            duration: Number.isFinite(video.duration) ? video.duration : 0,
          });
        };
        video.onerror = () => {
          clearTimeout(timer);
          reject(new Error("The video could not be read."));
        };
      },
    );

    // A frame at ~10% is less likely to be black than frame zero (fades, title
    // cards), and still represents the clip.
    const seekTo = Math.min(1, Math.max(0.05, meta.duration * 0.1));
    await new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, 8_000);
      video.onseeked = () => {
        clearTimeout(timer);
        resolve();
      };
      video.currentTime = seekTo;
    });

    const thumb = await renderThumb(video, meta.width, meta.height);
    return { thumb, width: meta.width, height: meta.height, durationSec: Math.round(meta.duration) };
  } catch {
    return { thumb: null, width: 0, height: 0, durationSec: 0 };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Prepare one selected file for upload.
 *
 * Never throws for an unsupported derivative: the original upload is the
 * product, and the thumbnail is an optimisation. A decode failure returns a
 * null thumb and zero dimensions, which the ticket validator already accepts.
 *
 * The still-image decision tree lives in `prepareImage` (web/src/lib/
 * upload-prepare.ts), where it is pinned by a Node harness; this file binds the
 * real browser effects to that policy. The ordering is the optimisation: the
 * native decode is attempted FIRST, so a Safari guest's HEIC never triggers the
 * ~2 MB `heic2any` WASM import or a JPEG re-encode — the original is uploaded
 * as-is and the phone does no conversion work at all.
 */
export async function prepareMedia(input: File): Promise<PreparedMedia> {
  if (input.type.startsWith("audio/")) {
    const url = URL.createObjectURL(input);
    const audio = document.createElement("audio");
    audio.preload = "metadata";
    audio.src = url;
    let durationSec = 0;
    try {
      durationSec = await new Promise<number>((resolve) => {
        const timer = window.setTimeout(() => resolve(0), 8_000);
        audio.onloadedmetadata = () => {
          window.clearTimeout(timer);
          resolve(Number.isFinite(audio.duration) ? audio.duration : 0);
        };
        audio.onerror = () => {
          window.clearTimeout(timer);
          resolve(0);
        };
      });
    } finally {
      URL.revokeObjectURL(url);
    }
    return { file: input, width: 0, height: 0, durationSec: Math.round(durationSec), thumb: null };
  }

  if (input.type.startsWith("video/")) {
    const poster = await videoPoster(input);
    return {
      file: input,
      width: poster.width,
      height: poster.height,
      durationSec: poster.durationSec,
      thumb: poster.thumb,
    };
  }

  if (!input.type.startsWith("image/") || input.type === "image/gif") {
    // GIF stays animated on the wall; a still thumbnail would be a downgrade.
    return { file: input, width: 0, height: 0, durationSec: 0, thumb: null };
  }

  const isHeic = HEIC_TYPE.test(input.type) || HEIC_NAME.test(input.name);
  const prepared = await prepareImage(input, isHeic, {
    decode: decodeImage,
    convertHeic: normaliseHeic,
    dimensions,
    thumb: async (source) => {
      const { width, height } = dimensions(source);
      const thumb = await renderThumb(source, width, height);
      // A bitmap holds decoded pixels until it is closed; an <img> element does
      // not need an explicit release.
      if (source instanceof ImageBitmap) source.close();
      return thumb;
    },
  });

  return { ...prepared, durationSec: 0 };
}

/** PUT the derivative through the Worker. Small enough that progress is noise. */
export async function uploadThumbnail(
  ticketId: string,
  slug: string,
  blob: Blob,
): Promise<void> {
  const response = await fetch(
    `/api/upload-thumb/${encodeURIComponent(ticketId)}?slug=${encodeURIComponent(slug)}`,
    {
      method: "PUT",
      headers: { "content-type": blob.type || "image/webp" },
      body: blob,
    },
  );

  if (!response.ok) {
    throw new Error(`Thumbnail upload failed (${response.status}).`);
  }
}
